import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'

// Sample Output Types
interface OutputData {
  id: string
  type: string
  typeColor: { background: string; color: string }
  title: string
  format: string
  content: LetterContent | SuitabilityContent
}

interface LetterContent {
  firmName: string
  firmAddress: string
  firmTel: string
  firmEmail: string
  date: string
  clientName: string
  clientAddress: string
  subject: string
  body: string
}

interface SuitabilityContent {
  title: string
  subtitle: string
  client: string
  adviser: string
  date: string
  ref: string
  sections: Array<{ title: string; content: string }>
}

// Sample Output Data
const sampleOutputs: { letter: OutputData; suitability: OutputData } = {
  letter: {
    id: 'letter',
    type: 'Basic',
    typeColor: { background: 'rgba(20, 184, 166, 0.15)', color: '#14b8a6' },
    title: 'Client Letter',
    format: 'PDF',
    content: {
      firmName: 'Mitchell Wealth Partners',
      firmAddress: '15 Queen Street, Bristol BS1 4NT',
      firmTel: '0117 456 7890',
      firmEmail: 'advice@mitchellwealth.co.uk',
      date: '2 December 2024',
      clientName: 'Mr James Richardson',
      clientAddress: '42 Clifton Gardens\nBristol\nBS8 4PQ',
      subject: 'Confirmation of Our Recent Meeting',
      body: `Dear Mr Richardson,

Thank you for meeting with me on 28 November 2024 to discuss your retirement planning requirements. I very much appreciated the opportunity to understand your financial situation and objectives in more detail.

As discussed, I have now completed my initial assessment of your circumstances and have prepared a comprehensive suitability report setting out my recommendations. The key points we covered include:

• Your target retirement age of 62 and income requirements of £35,000 per annum
• The consolidation of your existing pension arrangements with Scottish Widows, Aviva, and your workplace scheme
• Your attitude to investment risk, which I have assessed as 'Balanced'
• The importance of maintaining flexibility for your wife Sarah's future planning needs

I am recommending a pension consolidation into a SIPP arrangement with Transact, which will provide the investment flexibility and drawdown options we discussed. Full details of my rationale, the charges comparison, and risk warnings are contained in the enclosed suitability report.

Before proceeding, I would ask that you read the suitability report carefully and contact me if you have any questions. If you are happy to proceed, please sign and return the enclosed application forms along with the letter of authority.

Please do not hesitate to contact me if you would like to discuss any aspect of this recommendation further.

Yours sincerely,


Sarah Mitchell DipPFS
Director
Mitchell Wealth Partners

Enc: Suitability Report, Application Forms, Letter of Authority

Mitchell Wealth Partners is authorised and regulated by the Financial Conduct Authority. FCA Register Number: 123456`,
    } as LetterContent,
  },
  suitability: {
    id: 'suitability',
    type: 'Comprehensive',
    typeColor: { background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' },
    title: 'Suitability Report',
    format: 'PDF',
    content: {
      title: 'Suitability Report',
      subtitle: 'Pension Consolidation & Retirement Planning',
      client: 'Mr James Richardson',
      adviser: 'Sarah Mitchell DipPFS',
      date: '2 December 2024',
      ref: 'SR-2024-00847',
      sections: [
        {
          title: 'Executive Summary',
          content: `This report sets out my recommendations following our meeting on 28 November 2024 to discuss your retirement planning objectives. I am grateful for the time you took to explain your financial situation, goals, and concerns in detail, which has enabled me to prepare this comprehensive analysis and recommendation.

Based on my thorough assessment of your circumstances, objectives, and attitude to risk, I am recommending that you consolidate your three existing pension arrangements (Scottish Widows, Aviva, and Rolls-Royce Retirement Savings Plan) into a Self-Invested Personal Pension (SIPP) with Transact.

The total value of pensions being consolidated is £487,250. My recommendation involves transitioning to a diversified multi-asset portfolio through the Vanguard LifeStrategy 60% Equity Fund, which is aligned with your Balanced risk profile.`,
        },
        {
          title: 'Your Personal Circumstances',
          content: `CLIENT DETAILS
Name: Mr James William Richardson
Date of Birth: 15 March 1967 (Age 57)
Marital Status: Married to Sarah Richardson (Age 54)
Dependents: Two adult children - Thomas (28) and Emily (25)

EMPLOYMENT & INCOME
Current Employer: Rolls-Royce plc
Position: Senior Project Manager
Annual Gross Salary: £78,500
Expected Retirement Age: 62 (March 2029)

STATE PENSION ENTITLEMENT
Your State Pension Age: 67 (March 2034)
Forecast Annual State Pension: £10,600`,
        },
        {
          title: 'Your Financial Objectives',
          content: `PRIMARY OBJECTIVES

1. RETIREMENT INCOME
Retire at age 62 (March 2029) with a gross income of £35,000 per annum. You wish this income to increase broadly in line with inflation to maintain your purchasing power throughout retirement.

2. PENSION CONSOLIDATION
Consolidate your existing pension arrangements to simplify administration, reduce paperwork, and achieve a clearer overview of your retirement savings.

3. CHARGE REDUCTION
Reduce the ongoing charges on your pension savings to maximise the amount available for retirement income.

4. INCOME FLEXIBILITY
Maintain flexibility to adjust your income in retirement, particularly during the period from age 62 to 67 before your State Pension commences.`,
        },
        {
          title: 'Existing Pension Arrangements',
          content: `SCOTTISH WIDOWS PERSONAL PENSION
Current Fund Value: £142,800
Total Ongoing Charge: 1.65% per annum

AVIVA STAKEHOLDER PENSION
Current Fund Value: £89,450
Total Ongoing Charge: 1.00% per annum

ROLLS-ROYCE RETIREMENT SAVINGS PLAN
Current Fund Value: £255,000
Total Ongoing Charge: 0.45% per annum

[TABLE]
Provider | Fund Value | Charge Rate | Annual Cost
Scottish Widows | £142,800 | 1.65% | £2,356
Aviva | £89,450 | 1.00% | £894
Rolls-Royce | £255,000 | 0.45% | £1,148
TOTAL | £487,250 | 0.90% avg | £4,398
[/TABLE]`,
        },
        {
          title: 'My Recommendation',
          content: `I recommend that you transfer all three existing pension arrangements into a Transact SIPP and invest in the Vanguard LifeStrategy 60% Equity Fund.

RECOMMENDED ARRANGEMENT
Platform Provider: Transact (IntegraFin Holdings plc)
Product Type: Self-Invested Personal Pension
Transfer Amount: £487,250

COMPARISON WITH CURRENT ARRANGEMENTS

[TABLE]
Component | Current | Recommended | Saving
Platform/Admin Charges | £4,454 | £862 | £3,592
Fund/Investment Charges | £2,465 | £1,072 | £1,393
Total Annual Charges | £6,919 (1.42%) | £1,934 (0.40%) | £4,985
[/TABLE]

ANNUAL SAVING: £4,985 (1.02% of fund value)`,
        },
        {
          title: 'Risks and Considerations',
          content: `Before proceeding with this recommendation, it is important that you understand and accept the following risks:

INVESTMENT RISK
The value of your pension investments can go down as well as up. You may get back less than you invest.

TRANSFER RISK
By transferring from your existing arrangements, you will lose access to any features specific to those schemes.

INFLATION RISK
Your target income of £35,000 per annum will purchase less in the future due to inflation.

DRAWDOWN RISK
Unlike an annuity, drawdown does not provide a guaranteed income for life.`,
        },
      ],
    } as SuitabilityContent,
  },
}

// Guide Types
type MockupType =
  | 'sidebar'
  | 'button'
  | 'template-grid'
  | 'client-selector'
  | 'editor'
  | 'actions'
  | 'meeting-schedule'
  | 'meeting-settings'
  | 'meeting-start'
  | 'meeting-live'
  | 'meeting-end'
  | 'transcript-view'

interface GuideStep {
  id: number
  title: string
  description: string
  tip: string
  mockupType: MockupType
}

interface Guide {
  id: string
  title: string
  description: string
  gradient: string
  estimatedTime: string
  icon: 'document' | 'video'
  steps: GuideStep[]
}

// Interactive Walkthrough Guides Data
const guides: Guide[] = [
  {
    id: 'report',
    title: 'Create Your First AI Report',
    description:
      'See how WealthSpace transforms your meeting notes into professional suitability reports in minutes.',
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
    estimatedTime: '5 min',
    icon: 'document',
    steps: [
      {
        id: 1,
        title: 'Navigate to Reports',
        description:
          'From the sidebar, find "Reports" under the Client Management section. This is your document hub showing generated reports, templates, and drafts in progress.',
        tip: 'Use keyboard shortcut Ctrl+B to toggle the sidebar between expanded and compact views.',
        mockupType: 'sidebar',
      },
      {
        id: 2,
        title: 'Click "New Report"',
        description:
          'Click the "New Report" button in the top-right. This opens the report wizard where you\'ll select your template and attach source materials.',
        tip: 'You can also start a report directly from a completed meeting by clicking "Create Report" in the meeting detail view.',
        mockupType: 'button',
      },
      {
        id: 3,
        title: 'Select a Template',
        description:
          'Browse templates by type: Simple (quick file notes), Standard (structured suitability reports), Complex (premium multi-section documents), or Presentation (client-facing slideshows).',
        tip: 'Complex templates support custom headers/footers with your firm branding. Create your own in the Templates tab.',
        mockupType: 'template-grid',
      },
      {
        id: 4,
        title: 'Attach Source Materials',
        description:
          'Link meeting recordings, transcripts, file notes, or Rambles (voice dictations) as source materials. WealthSpace extracts client data, objectives, and recommendations from these inputs.',
        tip: 'The more context you provide, the better the AI output. Include previous reports for consistent tone and style.',
        mockupType: 'client-selector',
      },
      {
        id: 5,
        title: 'AI Generates Your Report',
        description:
          'WealthSpace processes your sources, extracting key facts and generating each section. Complex reports may take 2-3 minutes. Review the live progress bar as sections are completed.',
        tip: 'Click any section to edit in the rich text editor. Your edits are saved automatically.',
        mockupType: 'editor',
      },
      {
        id: 6,
        title: 'Export & Share',
        description:
          'Choose your output format: PDF download, Word document, or share via the secure client Portal. For Premium reports, clients can view an interactive flipbook experience.',
        tip: 'Enable Portal sharing in Settings to give clients secure login access to their documents.',
        mockupType: 'actions',
      },
    ],
  },
  {
    id: 'meeting',
    title: 'Record a Client Meeting',
    description:
      'Learn how WealthSpace captures your meetings with AI transcription, summaries, and action item extraction.',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    estimatedTime: '4 min',
    icon: 'video',
    steps: [
      {
        id: 1,
        title: 'Schedule the Meeting',
        description:
          'Go to Meetings in the Client Management section and click the "+" button. Select a client from your book, choose date, time, duration, and meeting type (Annual Review, New Client, etc.).',
        tip: 'Connect Outlook or Google Calendar in Settings to sync appointments and auto-create calendar invites with video links.',
        mockupType: 'meeting-schedule',
      },
      {
        id: 2,
        title: 'Choose Your Meeting Medium',
        description:
          "Select how you'll meet: In Person, Phone Call, Microsoft Teams, Google Meet, or Zoom. For video meetings, WealthSpace can auto-generate the meeting link when calendar sync is enabled.",
        tip: 'Each medium has different capture options. Virtual meetings can use our bot to join and record automatically.',
        mockupType: 'meeting-settings',
      },
      {
        id: 3,
        title: 'Capture the Meeting',
        description:
          'Open the meeting and click "Capture". Choose your method: record in-person audio, join virtual meeting with our recording bot, dial in via phone, or upload an existing recording file.',
        tip: 'For in-person meetings, simply tap the microphone to start recording. For virtual meetings, paste the meeting URL to have our bot join.',
        mockupType: 'meeting-start',
      },
      {
        id: 4,
        title: 'Add Supporting Materials',
        description:
          "During or after the meeting, upload photos of handwritten notes, attach relevant documents, or record a quick Ramble (voice dictation) summarising key points while they're fresh.",
        tip: 'Use the Rambles feature from the sidebar to capture quick voice notes anytime - great for post-meeting thoughts.',
        mockupType: 'meeting-live',
      },
      {
        id: 5,
        title: 'AI Processing',
        description:
          'Once captured, WealthSpace transcribes audio using AI (typically 5-10 minutes). The system identifies speakers, extracts key discussion points, and creates a structured file note.',
        tip: "You'll receive a notification when processing is complete. Continue with other work in the meantime.",
        mockupType: 'meeting-end',
      },
      {
        id: 6,
        title: 'Review & Generate Report',
        description:
          'View the full transcript with speaker labels, AI-generated summary, and structured file note sections. Edit any section, then click "Create Report" to generate a suitability report pre-filled with meeting data.',
        tip: 'File note sections are based on the meeting type template - customise these templates in Settings.',
        mockupType: 'transcript-view',
      },
    ],
  },
]

// Guide Mockup Component - renders UI mockups for each step
function GuideMockup({ mockupType }: { mockupType: MockupType }) {
  const mockups: Record<MockupType, React.ReactNode> = {
    // Report Guide Mockups
    sidebar: (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[280px] border border-white/10">
        <div className="text-xs text-brand-subtle uppercase tracking-wider mb-4 font-medium">Client Management</div>
        <div className="space-y-1">
          {[
            { icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', label: 'Clients', active: false },
            { icon: 'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18', label: 'Meetings', active: false },
            { icon: 'M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2', label: 'Rambles', active: false },
            { icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z', label: 'Reports', active: true },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${item.active ? 'bg-accent-teal text-[#0f1318] font-semibold' : 'text-brand-muted hover:bg-white/5'}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 shrink-0">
                <path d={item.icon} />
              </svg>
              <span>{item.label}</span>
              {item.active && (
                <span className="ml-auto text-[10px] bg-white/25 text-[#0f1318] px-2 py-0.5 rounded font-bold uppercase">
                  Click
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    ),

    button: (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[320px] border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <span className="text-base font-medium text-brand-light">Reports</span>
          <button className="text-sm bg-accent-teal text-[#0f1318] px-4 py-2 rounded-lg font-semibold animate-pulse flex items-center gap-1.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Report
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-white/5 rounded-lg border border-white/10 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-brand-subtle">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
            </div>
          ))}
        </div>
      </div>
    ),

    'template-grid': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[340px] border border-white/10">
        <div className="text-sm text-brand-muted mb-4">Choose Template Type</div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Simple', desc: 'Quick notes', color: '#14b8a6', active: true },
            { label: 'Standard', desc: 'Full report', color: '#10b981', active: false },
            { label: 'Complex', desc: 'Multi-section', color: '#8b5cf6', active: false },
          ].map((t, i) => (
            <div key={i} className={`p-4 rounded-xl text-center transition-all ${t.active ? 'bg-accent-teal/15 border-2 border-accent-teal' : 'bg-white/5 border border-white/10'}`}>
              <div className="w-10 h-10 mx-auto mb-2 rounded-lg flex items-center justify-center" style={{ background: `${t.color}25` }}>
                <svg viewBox="0 0 24 24" fill="none" stroke={t.color} strokeWidth="2" className="w-5 h-5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
              </div>
              <div className={`text-sm font-medium ${t.active ? 'text-accent-teal' : 'text-brand-light'}`}>{t.label}</div>
              <div className="text-[11px] text-brand-subtle mt-0.5">{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    ),

    'client-selector': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[320px] border border-white/10">
        <div className="text-sm text-brand-muted mb-4">Attach Source Materials</div>
        <div className="space-y-2">
          {[
            { icon: 'mic', label: 'Annual Review Recording', meta: '45 min audio', active: true },
            { icon: 'doc', label: 'Post-Meeting Ramble', meta: '5 min voice note', active: false },
            { icon: 'file', label: 'Previous Suitability', meta: 'PDF document', active: false },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-lg transition-all ${item.active ? 'bg-accent-teal/10 border border-accent-teal/40' : 'bg-white/5 border border-transparent opacity-60'}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${item.active ? 'bg-accent-teal/20' : 'bg-purple-500/20'}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke={item.active ? '#14b8a6' : '#8b5cf6'} strokeWidth="2" className="w-5 h-5">
                  {item.icon === 'mic' && <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />}
                  {item.icon === 'doc' && <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8" />}
                  {item.icon === 'file' && <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6" />}
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${item.active ? 'text-brand-light' : 'text-brand-muted'}`}>{item.label}</div>
                <div className="text-xs text-brand-subtle">{item.meta}</div>
              </div>
              {item.active && (
                <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-5 h-5 shrink-0">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    ),

    editor: (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[340px] border border-white/10">
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
          {['B', 'I', 'U', '|', 'H1', 'H2', '|', 'AI'].map((btn, i) => (
            <span key={i} className={`text-xs px-2.5 py-1.5 rounded ${btn === '|' ? 'text-white/20' : btn === 'AI' ? 'bg-accent-teal/20 text-accent-teal font-semibold' : 'bg-white/10 text-brand-muted hover:bg-white/15'}`}>
              {btn === '|' ? '' : btn}
            </span>
          ))}
        </div>
        <div className="bg-accent-teal/5 border border-accent-teal/30 rounded-lg p-4">
          <div className="text-sm font-semibold text-brand-light mb-2">Executive Summary</div>
          <div className="text-xs text-brand-muted leading-relaxed mb-3">
            Following our annual review meeting on 28 November, I am pleased to present my recommendations for your pension consolidation...
          </div>
          <div className="flex items-center gap-2 text-xs text-accent-teal bg-accent-teal/10 px-3 py-2 rounded-md w-fit">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Click to edit section
          </div>
        </div>
      </div>
    ),

    actions: (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[320px] border border-white/10">
        <div className="text-sm text-brand-muted mb-4">Export & Share</div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: 'PDF', icon: 'pdf', color: '#ef4444', active: true },
            { label: 'Word', icon: 'word', color: '#2563eb', active: false },
            { label: 'Portal', icon: 'portal', color: '#8b5cf6', active: false },
          ].map((opt, i) => (
            <div key={i} className={`p-4 rounded-xl text-center transition-all ${opt.active ? 'bg-accent-teal text-[#0f1318]' : 'bg-white/5 hover:bg-white/10'}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke={opt.active ? '#0f1318' : opt.color} strokeWidth="2" className="w-6 h-6 mx-auto mb-2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
              <span className={`text-sm font-medium ${opt.active ? '' : 'text-brand-light'}`}>{opt.label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 px-4 py-2.5 rounded-lg">
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
          Report ready for export
        </div>
      </div>
    ),

    // Meeting Guide Mockups
    'meeting-schedule': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="text-sm text-brand-muted mb-4">Schedule New Meeting</div>
        <div className="space-y-3">
          <div>
            <div className="text-xs text-brand-subtle mb-1.5">Client</div>
            <div className="flex items-center gap-3 bg-white/5 border border-accent-teal/40 rounded-lg px-3 py-2.5">
              <div className="w-8 h-8 rounded-full bg-accent-teal/20 flex items-center justify-center text-sm font-bold text-accent-teal">DT</div>
              <span className="text-sm text-brand-light font-medium">David Thompson</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-brand-subtle mb-1.5">Meeting Type</div>
            <div className="bg-white/5 rounded-lg px-3 py-2.5 text-sm text-brand-muted">Annual Review</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-brand-subtle mb-1.5">Date</div>
              <div className="bg-white/5 rounded-lg px-3 py-2 text-sm text-brand-muted">25 Jan 2026</div>
            </div>
            <div>
              <div className="text-xs text-brand-subtle mb-1.5">Time</div>
              <div className="bg-white/5 rounded-lg px-3 py-2 text-sm text-brand-muted">10:00 AM</div>
            </div>
          </div>
        </div>
      </div>
    ),

    'meeting-settings': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[340px] border border-white/10">
        <div className="text-sm text-brand-muted mb-4">Choose Meeting Location</div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'In Person', desc: 'Face to face', color: '#14b8a6', icon: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0zM12 7a3 3 0 100 6 3 3 0 000-6z', active: true },
            { label: 'Phone', desc: 'Voice call', color: '#f59e0b', icon: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72', active: false },
            { label: 'Video', desc: 'Teams/Zoom', color: '#6366f1', icon: 'M23 7l-7 5 7 5V7zM16 5H3a2 2 0 00-2 2v10a2 2 0 002 2h13a2 2 0 002-2V7a2 2 0 00-2-2z', active: false },
          ].map((opt, i) => (
            <div key={i} className={`p-4 rounded-xl text-center transition-all ${opt.active ? 'bg-accent-teal text-[#0f1318]' : 'bg-white/5'}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke={opt.active ? '#0f1318' : opt.color} strokeWidth="2" className="w-6 h-6 mx-auto mb-2">
                <path d={opt.icon} />
              </svg>
              <div className={`text-sm font-medium ${opt.active ? '' : 'text-brand-light'}`}>{opt.label}</div>
              <div className={`text-[11px] mt-0.5 ${opt.active ? 'opacity-70' : 'text-brand-subtle'}`}>{opt.desc}</div>
            </div>
          ))}
        </div>
      </div>
    ),

    'meeting-start': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[320px] border border-white/10">
        <div className="text-sm text-brand-muted mb-4">Select Capture Method</div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Record Audio', meta: 'In-person meeting', color: '#14b8a6', active: true },
            { label: 'Virtual Bot', meta: 'Teams, Meet, Zoom', color: '#6366f1', active: false },
          ].map((opt, i) => (
            <div key={i} className={`p-4 rounded-xl text-center transition-all ${opt.active ? 'bg-accent-teal text-[#0f1318]' : 'bg-white/5'}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke={opt.active ? '#0f1318' : opt.color} strokeWidth="2" className="w-8 h-8 mx-auto mb-2">
                {opt.active ? (
                  <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
                ) : (
                  <>
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" />
                  </>
                )}
              </svg>
              <div className={`text-sm font-medium ${opt.active ? '' : 'text-brand-light'}`}>{opt.label}</div>
              <div className={`text-[11px] mt-1 ${opt.active ? 'opacity-70' : 'text-brand-subtle'}`}>{opt.meta}</div>
            </div>
          ))}
        </div>
      </div>
    ),

    'meeting-live': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[300px] border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm font-medium text-brand-light">Recording in progress</span>
          <span className="text-sm text-brand-subtle ml-auto font-mono">12:34</span>
        </div>
        <div className="bg-white/5 rounded-lg p-4 mb-4">
          <div className="flex items-end justify-center gap-1 h-12">
            {[3, 5, 8, 4, 6, 9, 5, 3, 7, 4, 6, 8, 5, 3, 6].map((h, i) => (
              <div key={i} className="w-1.5 bg-accent-teal rounded-full transition-all" style={{ height: h * 4 }} />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/5 rounded-lg p-3 text-center hover:bg-white/10 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" className="w-5 h-5 mx-auto mb-1.5">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2zM12 13a3 3 0 100 6 3 3 0 000-6z" />
            </svg>
            <span className="text-xs text-brand-muted">Add Photo</span>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center hover:bg-white/10 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" className="w-5 h-5 mx-auto mb-1.5">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2" />
            </svg>
            <span className="text-xs text-brand-muted">Ramble</span>
          </div>
        </div>
      </div>
    ),

    'meeting-end': (
      <div className="bg-[#0f1318] rounded-xl p-6 w-full max-w-[280px] border border-white/10">
        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-full bg-accent-teal/20 flex items-center justify-center mx-auto mb-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="#14b8a6" strokeWidth="2" className="w-7 h-7 animate-spin">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
          <div className="text-base font-medium text-brand-light mb-1">Processing Recording</div>
          <div className="text-sm text-brand-subtle">Transcribing audio...</div>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-2">
          <div className="h-full bg-gradient-to-r from-accent-teal to-emerald-400 rounded-full transition-all" style={{ width: '65%' }} />
        </div>
        <div className="text-sm text-brand-muted text-center">65% complete</div>
      </div>
    ),

    'transcript-view': (
      <div className="bg-[#0f1318] rounded-xl p-5 w-full max-w-[320px] border border-white/10">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-brand-light">Meeting Transcript</span>
          <span className="text-xs bg-green-500/20 text-green-400 px-2.5 py-1 rounded-full font-medium">Complete</span>
        </div>
        <div className="space-y-3 mb-4">
          <div className="flex gap-3">
            <span className="text-xs text-accent-teal font-semibold shrink-0 w-16">Adviser:</span>
            <span className="text-sm text-brand-muted">Let's review your pension arrangements and discuss options...</span>
          </div>
          <div className="flex gap-3">
            <span className="text-xs text-purple-400 font-semibold shrink-0 w-16">Client:</span>
            <span className="text-sm text-brand-muted">Yes, I'd like to understand the consolidation benefits.</span>
          </div>
        </div>
        <div className="pt-4 border-t border-white/10">
          <button className="flex items-center gap-2 text-sm bg-accent-teal text-[#0f1318] px-4 py-2.5 rounded-lg font-semibold">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6" />
            </svg>
            Create Report
          </button>
        </div>
      </div>
    ),
  }

  return (
    <div className="flex items-center justify-center w-full">
      {mockups[mockupType]}
    </div>
  )
}

// Walkthrough Modal Component
function WalkthroughModal({ guide, onClose }: { guide: Guide; onClose: () => void }) {
  const [currentStep, setCurrentStep] = useState(1)

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const step = guide.steps.find((s) => s.id === currentStep)
  if (!step) return null

  const progress = (currentStep / guide.steps.length) * 100

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/90 flex items-start md:items-center justify-center overflow-y-auto py-4 px-3 md:p-6"
      onClick={onClose}
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-[#1a1f2e] rounded-2xl border border-white/[0.08] max-w-[900px] w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div
          className="p-5 md:p-6 shrink-0"
          style={{ background: guide.gradient }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                {guide.icon === 'document' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" className="w-5 h-5">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" className="w-5 h-5">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                )}
              </div>
              <div>
                <h2 className="font-serif text-lg md:text-xl font-medium text-white">{guide.title}</h2>
                <p className="text-sm text-white/70">Interactive Guide • {guide.estimatedTime}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white p-2 rounded-lg transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-5 md:px-6 py-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-brand-muted">
              Step {currentStep} of {guide.steps.length}
            </span>
            <span className="text-sm text-accent-teal font-medium">{Math.round(progress)}% complete</span>
          </div>
          <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: guide.gradient }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5 md:p-6 min-h-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mockup Area */}
            <div className="bg-[#15191f] rounded-xl p-6 flex items-center justify-center min-h-[200px]">
              <GuideMockup mockupType={step.mockupType} />
            </div>

            {/* Instructions */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white"
                  style={{ background: guide.gradient }}
                >
                  {currentStep}
                </div>
                <h3 className="font-serif text-xl font-medium text-brand-light">{step.title}</h3>
              </div>

              <p className="text-[15px] text-brand-muted leading-relaxed mb-6">{step.description}</p>

              {/* Tip Box */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3">
                <div className="shrink-0">
                  <svg viewBox="0 0 24 24" fill="#f59e0b" className="w-5 h-5">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <span className="text-xs font-semibold text-amber-500 uppercase tracking-wider">
                    Pro Tip
                  </span>
                  <p className="text-sm text-brand-muted mt-1">{step.tip}</p>
                </div>
              </div>

              {/* Step Indicators */}
              <div className="flex items-center justify-center gap-2 mt-6">
                {guide.steps.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setCurrentStep(s.id)}
                    className="w-2.5 h-2.5 rounded-full transition-all duration-200"
                    style={{
                      background:
                        s.id === currentStep
                          ? '#14b8a6'
                          : s.id < currentStep
                            ? 'rgba(20, 184, 166, 0.5)'
                            : 'rgba(255,255,255,0.1)',
                      transform: s.id === currentStep ? 'scale(1.3)' : 'scale(1)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between p-4 md:p-5 border-t border-white/[0.06] bg-black/20 shrink-0">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-brand-muted hover:text-brand-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Previous
          </button>

          {currentStep === guide.steps.length ? (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: guide.gradient }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              Complete Guide
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(Math.min(guide.steps.length, currentStep + 1))}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all"
              style={{ background: guide.gradient }}
            >
              Next Step
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

// Output Preview Modal Component
function OutputPreviewModal({
  output,
  onClose,
}: {
  output: OutputData | null
  onClose: () => void
}) {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (output) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [output])

  if (!output) return null

  // Parse content with table support
  const renderSectionContent = (content: string) => {
    const parts = content.split(/(\[TABLE\][\s\S]*?\[\/TABLE\])/g)

    return parts.map((part, idx) => {
      // Check if this is a table block
      if (part.startsWith('[TABLE]') && part.endsWith('[/TABLE]')) {
        const tableContent = part.replace('[TABLE]', '').replace('[/TABLE]', '').trim()
        const lines = tableContent.split('\n').filter((line) => line.trim())

        if (lines.length === 0) return null

        // First line is header
        const headers = lines[0].split('|').filter((cell) => cell.trim())
        const rows = lines.slice(1).map((line) => line.split('|').filter((cell) => cell.trim()))

        return (
          <div key={idx} className="my-4 -mx-2 sm:mx-0 overflow-x-auto">
            <table className="w-full border-collapse text-[11px] sm:text-sm bg-[#fafafa] rounded-lg overflow-hidden min-w-[400px]">
              <thead className="bg-gradient-to-r from-[#1a1f2e] to-[#2d3748] text-white">
                <tr>
                  {headers.map((header, hi) => (
                    <th
                      key={hi}
                      className="px-2 sm:px-4 py-2 sm:py-3 text-left font-semibold text-[10px] sm:text-[13px] whitespace-nowrap"
                    >
                      {header.trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  const isTotal =
                    row[0] && (row[0].includes('TOTAL') || row[0].includes('Total'))
                  return (
                    <tr
                      key={ri}
                      className={
                        isTotal
                          ? 'bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-white font-semibold'
                          : 'hover:bg-[#f0f0f0]'
                      }
                    >
                      {row.map((cell, ci) => {
                        const isSaving =
                          cell.includes('+') || cell.toLowerCase().includes('saving')
                        return (
                          <td
                            key={ci}
                            className={`px-2 sm:px-4 py-2 sm:py-3 border-b border-[#e5e5e5] last:border-b-0 whitespace-nowrap ${isSaving ? 'text-[#10b981] font-semibold' : ''}`}
                          >
                            {cell.trim()}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      }

      // Regular paragraphs
      if (!part.trim()) return null
      return part.split('\n\n').map((para, j) => {
        if (!para.trim()) return null
        // Check for subheadings (ALL CAPS lines)
        const lines = para.split('\n')
        if (lines.length === 1 && lines[0].toUpperCase() === lines[0] && lines[0].length < 50) {
          return (
            <h4
              key={`${idx}-${j}`}
              className="text-sm font-semibold text-[#0d9488] mt-5 mb-3 uppercase tracking-[0.5px]"
            >
              {para}
            </h4>
          )
        }
        return (
          <p key={`${idx}-${j}`} className="mb-3 leading-[1.7] whitespace-pre-wrap">
            {para}
          </p>
        )
      })
    })
  }

  const renderLetterContent = () => {
    const c = output.content as LetterContent
    return (
      <div className="bg-white text-[#1a1f2e] rounded-lg p-4 sm:p-6 md:p-10 lg:p-12 font-serif text-[13px] sm:text-sm leading-relaxed">
        {/* Letter Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8 pb-4 sm:pb-6 border-b-2 border-[#14b8a6]">
          <div className="flex items-center gap-2 sm:gap-3">
            <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-6 h-6 sm:w-8 sm:h-8 shrink-0">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
            </svg>
            <span className="font-serif text-base sm:text-lg md:text-xl font-semibold text-[#1a1f2e]">
              {c.firmName}
            </span>
          </div>
          <div className="text-left sm:text-right text-[11px] sm:text-xs text-gray-500 leading-relaxed">
            <div>{c.firmAddress}</div>
            <div>Tel: {c.firmTel}</div>
            <div className="break-all">Email: {c.firmEmail}</div>
          </div>
        </div>
        {/* Date */}
        <div className="mb-4 sm:mb-6">{c.date}</div>
        {/* Recipient */}
        <div className="mb-4 sm:mb-6 leading-relaxed">
          <div>{c.clientName}</div>
          <div className="whitespace-pre-line">{c.clientAddress}</div>
        </div>
        {/* Subject */}
        <div className="font-bold mb-4 sm:mb-6">Re: {c.subject}</div>
        {/* Body */}
        <div className="mb-4 sm:mb-6">
          {c.body.split('\n\n').map((para, i) => (
            <p key={i} className="mb-3 sm:mb-4 leading-[1.6] sm:leading-[1.7]">
              {para}
            </p>
          ))}
        </div>
      </div>
    )
  }

  const renderSuitabilityContent = () => {
    const c = output.content as SuitabilityContent
    return (
      <div className="bg-white text-[#1a1f2e] rounded-lg overflow-hidden">
        {/* Cover */}
        <div className="bg-gradient-to-br from-[#1a1f2e] to-[#2d3748] text-white p-5 sm:p-8 md:p-12 text-center">
          <div className="flex justify-center mb-4 sm:mb-6">
            <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-10 h-10 sm:w-12 sm:h-12">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
            </svg>
          </div>
          <h1 className="font-serif text-xl sm:text-2xl md:text-3xl font-medium mb-2">{c.title}</h1>
          <h2 className="text-sm sm:text-base md:text-lg text-white/70 mb-5 sm:mb-8">{c.subtitle}</h2>
          <div className="text-[12px] sm:text-sm text-white/60 space-y-1">
            <div>
              Prepared for: <strong className="text-white">{c.client}</strong>
            </div>
            <div>
              Prepared by: <strong className="text-white">{c.adviser}</strong>
            </div>
            <div>
              Date: <strong className="text-white">{c.date}</strong>
            </div>
            <div>
              Reference: <strong className="text-white">{c.ref}</strong>
            </div>
          </div>
        </div>
        {/* Table of Contents */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-gray-200">
          <h3 className="font-serif text-base sm:text-lg font-semibold text-[#1a1f2e] mb-3 sm:mb-4">
            Contents
          </h3>
          {c.sections.map((section, i) => (
            <div
              key={i}
              className="flex items-center justify-between py-1.5 sm:py-2 text-[12px] sm:text-sm"
            >
              <span className="text-[#1a1f2e] min-w-0 truncate pr-2">
                {i + 1}. {section.title}
              </span>
              <span className="hidden sm:block flex-1 border-b border-dotted border-gray-300 mx-3 shrink" />
              <span className="text-gray-500 shrink-0">{i + 2}</span>
            </div>
          ))}
        </div>
        {/* Sections */}
        {c.sections.map((section, i) => (
          <div key={i} className="p-4 sm:p-6 md:p-8 border-b border-gray-100 last:border-b-0">
            <h3 className="font-serif text-base sm:text-lg md:text-xl font-semibold text-[#1a1f2e] mb-3 sm:mb-4 pb-2 border-b-2 border-[#14b8a6]">
              {i + 1}. {section.title}
            </h3>
            <div className="text-[12px] sm:text-sm text-[#374151]">
              {renderSectionContent(section.content)}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/90 flex items-start md:items-center justify-center overflow-y-auto py-4 px-3 md:p-6"
      onClick={onClose}
      style={{ zIndex: 9999 }}
    >
      <div
        className="bg-gradient-to-br from-[#1e232d] to-[#15191f] rounded-2xl border border-white/[0.08] max-w-[900px] w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <span
              className="px-2 md:px-2.5 py-1 rounded text-[10px] md:text-xs font-semibold uppercase shrink-0"
              style={output.typeColor}
            >
              {output.type}
            </span>
            <h2 className="font-serif text-lg md:text-[22px] font-medium text-brand-light truncate">
              {output.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-brand-subtle hover:text-brand-muted p-2 rounded-lg transition-colors shrink-0 ml-2"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="w-5 h-5 md:w-6 md:h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-auto p-3 md:p-6 min-h-0">
          {output.id === 'letter' && renderLetterContent()}
          {output.id === 'suitability' && renderSuitabilityContent()}
        </div>
        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-3 md:p-4 border-t border-white/[0.06] bg-black/20 shrink-0">
          <span className="text-[11px] md:text-[13px] text-brand-subtle">
            Sample output based on UK financial adviser best practice
          </span>
          <span className="text-[11px] md:text-[13px] text-accent-teal font-medium">
            Format: {output.format}
          </span>
        </div>
      </div>
    </div>
  )

  // Render modal in portal at document body level
  return createPortal(modalContent, document.body)
}

export default function DemoPage() {
  const [selectedOutput, setSelectedOutput] = useState<OutputData | null>(null)
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null)

  return (
    <>
      <SEO
        title="WealthSpace Demo | See AI Adviser Software in Action"
        description="Explore sample outputs from WealthSpace. See how UK financial advisers save hours with AI-powered reports, compliance tools, and client portals."
        keywords="WealthSpace demo, financial adviser software demo, AI report demo, IFA software trial, practice management demo UK"
        url="/demo"
      />

      {/* Hero */}
      <section className="py-[100px] px-6 max-w-[1000px] mx-auto text-center relative z-[1]">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-teal/10 border border-accent-teal/20 mb-6 animate-fade-in-up opacity-0 [animation-delay:0.1s] [animation-fill-mode:forwards]">
          <span className="w-2 h-2 rounded-full bg-accent-teal animate-pulse" />
          <span className="text-sm font-medium text-accent-teal">Interactive walkthroughs</span>
        </div>
        <h1 className="font-serif text-[clamp(36px,5vw,52px)] font-normal leading-[1.2] tracking-[-0.02em] mb-5 animate-fade-in-up opacity-0 [animation-delay:0.15s] [animation-fill-mode:forwards]">
          See WealthSpace
          <br />
          <span className="text-accent-teal">in action</span>
        </h1>
        <p className="text-lg text-brand-muted leading-[1.7] max-w-[600px] mx-auto animate-fade-in-up opacity-0 [animation-delay:0.2s] [animation-fill-mode:forwards]">
          Experience the platform through guided, interactive demos. No sales calls, no sign-ups.
          Just click through and see how it works.
        </p>
      </section>

      {/* Interactive Guides Section */}
      <section className="py-10 px-6 relative z-[1]">
        <div className="max-w-[1000px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {guides.map((guide) => (
              <div
                key={guide.id}
                onClick={() => setSelectedGuide(guide)}
                className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:border-accent-teal/30 hover:shadow-[0_24px_48px_rgba(0,0,0,0.4)] group"
              >
                {/* Card Header/Thumbnail */}
                <div
                  className="relative p-6 h-40"
                  style={{ background: guide.gradient }}
                >
                  {/* Background pattern */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10" />
                    <div className="absolute -bottom-20 -left-10 w-60 h-60 rounded-full bg-black/10" />
                  </div>
                  {/* Content */}
                  <div className="relative flex items-center justify-center h-full">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                      {guide.icon === 'document' ? (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="2"
                          className="w-7 h-7"
                        >
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth="2"
                          className="w-7 h-7"
                        >
                          <polygon points="23 7 16 12 23 17 23 7" />
                          <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                        </svg>
                      )}
                    </div>
                    <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-white/20 text-[11px] font-semibold text-white uppercase tracking-wider">
                      Interactive
                    </span>
                  </div>
                  {/* Duration badge */}
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 text-[11px] font-medium text-white">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="w-3 h-3"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {guide.estimatedTime}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-1 rounded-full bg-accent-teal/15 text-[11px] font-semibold text-accent-teal uppercase">
                      Beginner
                    </span>
                    <span className="text-xs text-brand-subtle">{guide.steps.length} steps</span>
                  </div>
                  <h3 className="font-serif text-xl font-medium text-brand-light mb-2">
                    {guide.title}
                  </h3>
                  <p className="text-[15px] text-brand-muted leading-relaxed mb-5">
                    {guide.description}
                  </p>
                  <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/[0.08] text-sm font-medium text-brand-light transition-all group-hover:bg-accent-teal group-hover:border-accent-teal group-hover:text-white">
                    Start Guide
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="w-4 h-4"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Output Examples */}
      <section className="py-20 px-6 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold text-accent-teal uppercase tracking-[0.15em] mb-4 block">
              Real Output Examples
            </span>
            <h2 className="font-serif text-4xl font-normal text-brand-light mb-4">
              See what WealthSpace produces
            </h2>
            <p className="text-lg text-brand-muted max-w-[600px] mx-auto">
              From simple letters to comprehensive suitability reports, every output is professional,
              compliant, and ready to share.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Client Letter Card */}
            <div
              className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-accent-teal/30 hover:shadow-[0_24px_48px_rgba(0,0,0,0.4)] cursor-pointer group"
              onClick={() => setSelectedOutput(sampleOutputs.letter)}
            >
              {/* Preview section with mockup */}
              <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-6">
                {/* View Sample hint on hover */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <span className="flex items-center gap-2 text-sm font-medium text-white">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Sample
                  </span>
                </div>
                {/* Document mockup */}
                <div className="w-32 bg-white rounded shadow-lg p-3 transform rotate-[-2deg]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded bg-accent-teal/20 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-3 h-3">
                        <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <div className="h-1.5 bg-gray-200 rounded w-full mb-1" />
                      <div className="h-1 bg-gray-100 rounded w-3/4" />
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded w-2/3 mb-3" />
                  <div className="space-y-1.5 mb-3">
                    <div className="h-1 bg-gray-100 rounded" />
                    <div className="h-1 bg-gray-100 rounded" />
                    <div className="h-1 bg-gray-100 rounded w-4/5" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-1 bg-gray-100 rounded" />
                    <div className="h-1 bg-gray-100 rounded" />
                    <div className="h-1 bg-gray-100 rounded w-2/3" />
                  </div>
                  <div className="mt-4 pt-2 border-t border-gray-100">
                    <div className="h-1.5 bg-gray-200 rounded w-1/3 mb-1" />
                    <div className="h-1 bg-gray-100 rounded w-1/4" />
                  </div>
                </div>
                {/* PDF badge */}
                <div className="absolute top-3 right-3 text-[10px] font-semibold text-gray-500 bg-white px-2 py-0.5 rounded shadow-sm">
                  PDF
                </div>
              </div>
              {/* Info section */}
              <div className="p-6">
                <span
                  className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3"
                  style={{
                    background: 'rgba(20, 184, 166, 0.15)',
                    color: '#14b8a6',
                  }}
                >
                  Basic
                </span>
                <h3 className="font-serif text-xl font-medium text-brand-light mb-2">
                  Client Letter
                </h3>
                <p className="text-[15px] text-brand-muted leading-relaxed mb-4">
                  Clean, professional correspondence. Perfect for confirmations, cover letters, and
                  simple recommendations.
                </p>
                <div className="flex items-center gap-4 text-xs text-brand-muted">
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 opacity-60">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    </svg>
                    <span>1-2 pages</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 opacity-60">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span>~2 min</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Suitability Report Card */}
            <div
              className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-accent-teal/30 hover:shadow-[0_24px_48px_rgba(0,0,0,0.4)] cursor-pointer group"
              onClick={() => setSelectedOutput(sampleOutputs.suitability)}
            >
              {/* Preview section with mockup */}
              <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-6">
                {/* View Sample hint on hover */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <span className="flex items-center gap-2 text-sm font-medium text-white">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Sample
                  </span>
                </div>
                {/* Complex document mockup with stacked pages */}
                <div className="relative">
                  {/* Background pages */}
                  <div className="absolute top-2 left-2 w-32 h-40 bg-white rounded shadow-sm" />
                  <div className="absolute top-1 left-1 w-32 h-40 bg-white rounded shadow-sm" />
                  {/* Front cover */}
                  <div className="relative w-32 h-40 rounded shadow-lg overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1f2e 0%, #2d3748 100%)' }}>
                    <div className="p-3 h-full flex flex-col">
                      <div className="flex justify-center mb-3">
                        <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-5 h-5">
                          <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
                        </svg>
                      </div>
                      <div className="flex-1 flex flex-col justify-center items-center text-center">
                        <div className="h-2 bg-white/30 rounded w-3/4 mb-2" />
                        <div className="h-1.5 bg-white/20 rounded w-1/2 mb-4" />
                      </div>
                      <div className="mt-auto">
                        <div className="h-1 bg-white/15 rounded w-2/3 mx-auto mb-1" />
                        <div className="h-1 bg-white/10 rounded w-1/2 mx-auto" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* PDF badge */}
                <div className="absolute top-3 right-3 text-[10px] font-semibold text-gray-500 bg-white px-2 py-0.5 rounded shadow-sm">
                  PDF
                </div>
              </div>
              {/* Info section */}
              <div className="p-6">
                <span
                  className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3"
                  style={{
                    background: 'rgba(139, 92, 246, 0.15)',
                    color: '#a78bfa',
                  }}
                >
                  Comprehensive
                </span>
                <h3 className="font-serif text-xl font-medium text-brand-light mb-2">
                  Suitability Report
                </h3>
                <p className="text-[15px] text-brand-muted leading-relaxed mb-4">
                  Full suitability documentation with research, recommendations, risk analysis, and
                  FCA-compliant disclosures.
                </p>
                <div className="flex items-center gap-4 text-xs text-brand-muted">
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 opacity-60">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    </svg>
                    <span>15-40 pages</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 opacity-60">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span>~8 min</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Portal Presentation - Coming Soon Card */}
            <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl overflow-hidden opacity-70 cursor-default">
              {/* Preview section with mockup */}
              <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-6">
                {/* Coming Soon badge */}
                <div className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider text-[#0f1318] bg-gradient-to-br from-[#f59e0b] to-[#d97706] px-2.5 py-1 rounded z-10">
                  Coming Soon
                </div>
                {/* Portal presentation mockup - browser window style */}
                <div className="relative w-36 bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* Browser chrome */}
                  <div className="bg-gray-100 px-2 py-1.5 flex items-center gap-1.5 border-b border-gray-200">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <div className="w-2 h-2 rounded-full bg-yellow-400" />
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 bg-white rounded h-3 mx-1" />
                  </div>
                  {/* Portal content */}
                  <div className="p-2">
                    {/* Header with logo */}
                    <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-gray-100">
                      <div className="w-4 h-4 rounded bg-accent-teal/20 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-2.5 h-2.5">
                          <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
                        </svg>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded w-12" />
                    </div>
                    {/* Slides preview grid */}
                    <div className="grid grid-cols-2 gap-1">
                      <div className="aspect-[4/3] bg-gradient-to-br from-[#1a1f2e] to-[#2d3748] rounded-sm flex items-center justify-center">
                        <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-3 h-3">
                          <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"/>
                        </svg>
                      </div>
                      <div className="aspect-[4/3] bg-gray-50 rounded-sm p-1">
                        <div className="h-1 bg-gray-200 rounded w-3/4 mb-1" />
                        <div className="h-0.5 bg-gray-100 rounded mb-0.5" />
                        <div className="h-0.5 bg-gray-100 rounded w-2/3" />
                      </div>
                      <div className="aspect-[4/3] bg-gray-50 rounded-sm p-1 flex items-center justify-center">
                        <div className="flex gap-0.5 items-end">
                          <div className="w-1 h-2 bg-accent-teal/60 rounded-sm" />
                          <div className="w-1 h-3 bg-indigo-400/60 rounded-sm" />
                          <div className="w-1 h-2.5 bg-amber-400/60 rounded-sm" />
                        </div>
                      </div>
                      <div className="aspect-[4/3] bg-gray-50 rounded-sm p-1">
                        <div className="h-0.5 bg-gray-200 rounded mb-1" />
                        <div className="h-0.5 bg-gray-100 rounded mb-0.5" />
                        <div className="h-0.5 bg-gray-100 rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Interactive badge */}
                <div className="absolute top-3 right-3 text-[10px] font-semibold text-[#0f1318] bg-gradient-to-r from-[#14b8a6] to-[#10b981] px-2 py-0.5 rounded shadow-sm">
                  Interactive
                </div>
              </div>
              {/* Info section */}
              <div className="p-6">
                <span
                  className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3"
                  style={{
                    background: 'rgba(107, 114, 128, 0.15)',
                    color: '#6b7280',
                  }}
                >
                  Coming Soon
                </span>
                <h3 className="font-serif text-xl font-medium text-brand-light mb-2">
                  Portal Presentation
                </h3>
                <p className="text-[15px] text-brand-muted leading-relaxed mb-4">
                  Share interactive presentations with clients via your secure portal. Custom
                  branding, analytics, and a professional viewing experience.
                </p>
                <div className="flex items-center gap-4 text-xs text-brand-muted mb-4">
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 opacity-60">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                    <span>10-30 slides</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5 opacity-60">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span>~8 min</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="flex items-center gap-1 text-[11px] text-brand-subtle bg-white/[0.03] border border-white/[0.06] px-2 py-1 rounded">
                    <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-3 h-3">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                    Your branding
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-brand-subtle bg-white/[0.03] border border-white/[0.06] px-2 py-1 rounded">
                    <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-3 h-3">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                    View analytics
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-brand-subtle bg-white/[0.03] border border-white/[0.06] px-2 py-1 rounded">
                    <svg viewBox="0 0 24 24" fill="#14b8a6" className="w-3 h-3">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                    </svg>
                    Secure sharing
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 px-6 bg-black/15 relative z-[1]">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="font-serif text-3xl font-normal text-center mb-12">
            Built for <span className="text-accent-teal">real advisers</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '4hrs', label: 'Saved per client' },
              { value: '95%', label: 'Compliance ready' },
              { value: '10min', label: 'From meeting to report' },
              { value: '40+', label: 'Document types' },
            ].map((stat, index) => (
              <div
                key={index}
                className="animate-fade-in-up opacity-0 [animation-fill-mode:forwards]"
                style={{ animationDelay: `${0.5 + index * 0.1}s` }}
              >
                <div className="font-serif text-4xl text-accent-teal mb-2">{stat.value}</div>
                <div className="text-sm text-brand-muted">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 relative z-[1]">
        <div className="max-w-[700px] mx-auto text-center">
          <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] rounded-3xl py-16 px-12">
            <h2 className="font-serif text-[36px] font-normal tracking-[-0.02em] mb-5">
              Ready to see more?
            </h2>
            <p className="text-lg text-brand-muted leading-[1.7] mb-10 max-w-[450px] mx-auto">
              Book a personalised demo to see how WealthSpace can work for your practice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button href="/contact" size="lg">
                Book Your Demo
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Button>
              <Button href="/features" variant="secondary" size="lg">
                Explore Features
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="text-center px-6 pb-16 relative z-[1]">
        <Link
          to="/get-started"
          className="inline-flex items-center gap-2 text-sm text-brand-subtle hover:text-brand-muted transition-colors"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to options
        </Link>
      </div>

      {/* Walkthrough Modal */}
      {selectedGuide && (
        <WalkthroughModal
          key={selectedGuide.id}
          guide={selectedGuide}
          onClose={() => setSelectedGuide(null)}
        />
      )}

      {/* Output Preview Modal */}
      {selectedOutput && (
        <OutputPreviewModal output={selectedOutput} onClose={() => setSelectedOutput(null)} />
      )}
    </>
  )
}
